import Fuse from "fuse.js";
import { FilterDefinition } from "src/uniform/contenttypes/filterDefinition";
import { SortDefinition } from "src/uniform/contenttypes/sortDefinition";

import { FilterOptions, SubFilterObject, UniformFilterObject } from "./types";

export type OptionPair = { value: string; label: string };

const UNRANKED_PRIORITY = Number.MAX_SAFE_INTEGER;

/** Featured items (matched by `fields.fullUrlSlug`) lead in slug order; the rest keep their incoming order. */
export function prioritizeItemsBySlug(
    items: unknown[],
    featuredItemSlugs: string[]
): unknown[] {
    if (featuredItemSlugs.length === 0) return items;

    const priorityBySlug = new Map(
        featuredItemSlugs.map((slug, index) => [slug, index])
    );

    return [...items].sort(
        (a, b) =>
            getSlugPriority(a, priorityBySlug) -
            getSlugPriority(b, priorityBySlug)
    );
}

function getSlugPriority(
    item: unknown,
    priorityBySlug: Map<string, number>
): number {
    const slug = getItemFullUrlSlug(item);
    if (slug === undefined) return UNRANKED_PRIORITY;
    return priorityBySlug.get(slug) ?? UNRANKED_PRIORITY;
}

function getItemFullUrlSlug(item: unknown): string | undefined {
    if (!isRecord(item)) return undefined;
    const fields = item["fields"];
    if (!isRecord(fields)) return undefined;
    return normalizeUrlSlug(fields["fullUrlSlug"]);
}

/** Normalises a raw slug/URL value to a leading-slash pathname (no query/hash/trailing slash). */
export function normalizeUrlSlug(value: unknown): string | undefined {
    if (typeof value !== "string") return undefined;

    const trimmedValue = value.trim();
    if (trimmedValue.length === 0) return undefined;

    const pathname = extractPathname(trimmedValue);
    return stripTrailingSlash(stripQueryAndFragment(pathname));
}

function extractPathname(value: string): string {
    const valueWithProtocol = value.startsWith("//") ? `https:${value}` : value;
    try {
        return new URL(valueWithProtocol).pathname;
    } catch {
        return value.startsWith("/") ? value : `/${value}`;
    }
}

function stripQueryAndFragment(path: string): string {
    return path.split(/[?#]/, 1)[0] ?? path;
}

function stripTrailingSlash(path: string): string {
    if (path === "/") return path;
    return path.endsWith("/") ? path.slice(0, -1) : path;
}

function isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === "object" && value !== null;
}

type OptionItem = string | { value: string; label?: string };

/** Fuse search config (Uniform JSON param). `keys` map item dot-paths to a weight (0–1). */
export interface SearchConfiguration {
    readonly keys: ReadonlyArray<{
        readonly name: string;
        readonly weight: number;
    }>;
    readonly threshold?: number;
    readonly ignoreLocation?: boolean;
    /** Featured score bonus (0–1): featured leads matches within this of its score; a match better by more wins. */
    readonly featuredBoost?: number;
}

const DEFAULT_SEARCH_THRESHOLD = 0.4;
const DEFAULT_FEATURED_BOOST = 0.15;

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

function isPrimitive(v: unknown): v is string | number | boolean {
    return v !== null && v !== undefined && typeof v !== "object";
}

function isUnresolvedLink(value: unknown): boolean {
    if (typeof value !== "object" || value === null) return false;
    const sys = (value as Record<string, unknown>)["sys"];
    return (
        typeof sys === "object" &&
        sys !== null &&
        (sys as Record<string, unknown>)["type"] === "Link"
    );
}

export function coerceToPrimitiveArray(value: unknown): string[] {
    const values = Array.isArray(value) ? value : [value];
    return values.filter(isPrimitive).map(String);
}

function normaliseOptionItems(
    options: string | OptionItem[] | null | undefined
): OptionItem[] {
    if (!options) return [];
    if (Array.isArray(options)) return options;
    try {
        const parsed = JSON.parse(options);
        return Array.isArray(parsed) ? parsed : [];
    } catch {
        return [];
    }
}

function optionItemToPair(o: OptionItem): OptionPair {
    return typeof o === "string"
        ? { value: o, label: o }
        : { value: String(o.value), label: o.label ?? String(o.value) };
}

// ---------------------------------------------------------------------------
// resolveFieldPath
// ---------------------------------------------------------------------------

/**
 * Resolves a dot-path against a data item. Supports array spreads via `[]`
 * (e.g. "fields.category[].fields.urlSlug"). Returns `undefined` for missing
 * or unresolved Contentful Link nodes; never throws.
 */
export function resolveFieldPath(
    item: unknown,
    path: string
): unknown | unknown[] {
    const segments = path.split(/\[\]\.?/);

    function walk(value: unknown, segs: string[]): unknown | unknown[] {
        if (segs.length === 0) return value;

        const [head, ...tail] = segs;
        if (head === undefined) return value;

        let current: unknown = value;

        for (const key of head.split(".").filter(Boolean)) {
            if (current === null || current === undefined) return undefined;
            if (typeof current !== "object") return undefined;
            // Block navigation into `fields` of an unresolved Link (the linked entry hasn't
            // been resolved so its `fields` is empty/missing). But allow navigating into `sys`
            // (e.g. sys.id) — that data is always present on Link stubs and is intentional.
            if (isUnresolvedLink(current) && key !== "sys") return undefined;
            current = (current as Record<string, unknown>)[key];
        }

        if (tail.length === 0) return current;
        if (!Array.isArray(current)) return undefined;

        const results = current.flatMap((el) => {
            const resolved = walk(el, tail);
            return Array.isArray(resolved) ? resolved : [resolved];
        });

        return results.filter((v) => v !== undefined);
    }

    return walk(item, segments);
}

// ---------------------------------------------------------------------------
// Option parsing (explicit override)
// ---------------------------------------------------------------------------

/** Parses FilterDefinition.options into value strings. Accepts a pre-parsed array or JSON string. */
export function parseOptions(
    options: string | OptionItem[] | null | undefined
): string[] {
    return normaliseOptionItems(options).map((o) =>
        typeof o === "string" ? o : String(o.value)
    );
}

/** Parses FilterDefinition.options into { value, label } pairs. */
export function parseOptionsWithLabels(
    options: string | OptionItem[] | null | undefined
): OptionPair[] {
    return normaliseOptionItems(options).map(optionItemToPair);
}

// ---------------------------------------------------------------------------
// Option derivation (auto-derive from items)
// ---------------------------------------------------------------------------

/** Collects unique primitive values at `fieldPath` across all items, sorted alphabetically. */
export function deriveOptions(items: unknown[], fieldPath: string): string[] {
    const seen = new Set<string>();
    for (const item of items) {
        for (const v of coerceToPrimitiveArray(
            resolveFieldPath(item, fieldPath)
        )) {
            seen.add(v);
        }
    }
    return Array.from(seen).sort((a, b) => a.localeCompare(b));
}

/**
 * Like `deriveOptions`, but pairs each value with a display label from a
 * parallel `labelFieldPath`. Falls back to the value when the label doesn't resolve.
 */
export function deriveOptionsWithLabels(
    items: unknown[],
    fieldPath: string,
    labelFieldPath: string
): OptionPair[] {
    // Map<label, Set<value>> — groups multiple IDs under the same display label.
    // This handles cases where distinct entries share a label (e.g. "Design" for
    // both Jira and Confluence categories). The combined IDs are pipe-separated so
    // matchValue can split and OR-match all of them.
    const labelToIds = new Map<string, Set<string>>();

    for (const item of items) {
        const valueArr = coerceToPrimitiveArray(
            resolveFieldPath(item, fieldPath)
        );
        const labelArr = coerceToPrimitiveArray(
            resolveFieldPath(item, labelFieldPath)
        );

        valueArr.forEach((valueStr, i) => {
            const label = labelArr[i] ?? valueStr;
            if (!labelToIds.has(label)) {
                labelToIds.set(label, new Set());
            }
            labelToIds.get(label)?.add(valueStr);
        });
    }

    return Array.from(labelToIds.entries())
        .map(([label, ids]) => ({ value: Array.from(ids).join("|"), label }))
        .sort((a, b) => a.label.localeCompare(b.label));
}

// ---------------------------------------------------------------------------
// Shared resolution (used by both buildUniformFilterObject and buildLabelMaps)
// ---------------------------------------------------------------------------

function resolveOptionPairs(
    items: unknown[],
    filter: FilterDefinition,
    optionsDataSources?: Record<string, unknown[]>
): OptionPair[] {
    // 1. Explicit static options — highest priority.
    if (filter.options) {
        const pairs = parseOptionsWithLabels(filter.options);
        if (pairs.length > 0) return pairs;
    }
    // 2. Data-bound options from a gallery-level data resource — resolves the
    //    limitation of data bindings inside $block children not being resolved
    //    by Uniform before flattenValues runs. The gallery component receives
    //    the resolved array as a top-level prop and passes it here keyed by
    //    filter ID.
    const externalData = optionsDataSources?.[filter.id];
    if (Array.isArray(externalData) && externalData.length > 0) {
        if (!filter.optionsValuePath) {
            // eslint-disable-next-line no-console
            console.warn(
                `[FilterableGallery] FacetDataSource found for filter "${filter.id}" but optionsValuePath is not set. ` +
                    `Set Options Value Path on the FilterDefinition (e.g. "sys.id"). Falling back to auto-derive.`
            );
        } else {
            return deriveOptionsWithLabels(
                externalData,
                filter.optionsValuePath,
                filter.optionsLabelPath ?? filter.optionsValuePath
            );
        }
    }
    // 3. Auto-derive from gallery items — fallback.
    if (filter.labelFieldPath) {
        return deriveOptionsWithLabels(
            items,
            filter.fieldPath,
            filter.labelFieldPath
        );
    }
    return deriveOptions(items, filter.fieldPath).map((v) => ({
        value: v,
        label: v
    }));
}

// ---------------------------------------------------------------------------
// State initialisation
// ---------------------------------------------------------------------------

/** Builds the initial UniformFilterObject from FilterDefinitions + items. All options start inactive. */
export function buildUniformFilterObject(
    items: unknown[],
    filters: FilterDefinition[],
    optionsDataSources?: Record<string, unknown[]>
): UniformFilterObject {
    return Object.fromEntries(
        filters.map((filter) => {
            const subFilter: SubFilterObject = Object.fromEntries(
                resolveOptionPairs(items, filter, optionsDataSources).map(
                    ({ value }) => [
                        value,
                        {
                            committedActive: false,
                            stagedActive: false
                        } satisfies FilterOptions
                    ]
                )
            );
            return [filter.id, subFilter];
        })
    );
}

/** Builds a filterId → { value: displayLabel } map for all filters. */
export function buildLabelMaps(
    items: unknown[],
    filters: FilterDefinition[],
    optionsDataSources?: Record<string, unknown[]>
): Record<string, Record<string, string>> {
    return Object.fromEntries(
        filters.map((filter) => [
            filter.id,
            Object.fromEntries(
                resolveOptionPairs(items, filter, optionsDataSources).map(
                    ({ value, label }) => [value, label]
                )
            )
        ])
    );
}

// ---------------------------------------------------------------------------
// Filter pipeline
// ---------------------------------------------------------------------------

function matchValue(
    itemValue: unknown,
    matchType: FilterDefinition["matchType"],
    selected: string[]
): boolean {
    const itemValues = coerceToPrimitiveArray(itemValue);

    // Selected values may be pipe-separated IDs (e.g. "id1|id2") when multiple
    // entries share the same display label (see deriveOptionsWithLabels).
    // Each pipe-joined token represents OR semantics — the item matches if it
    // contains ANY of the IDs in the token.
    if (matchType === "equals") {
        // Item matches if it satisfies at least one selected token (OR across tokens,
        // OR across pipe-joined IDs within each token).
        return selected.some((token) =>
            token.split("|").some((id) => itemValues.includes(id))
        );
    }
    if (matchType === "includes") {
        // Item matches if it satisfies every selected token. Within each token,
        // pipe-joined IDs are OR'd (same label = different product variants).
        return selected.every((token) =>
            token.split("|").some((id) => itemValues.includes(id))
        );
    }
    if (matchType === "contains") {
        return selected.some((token) =>
            token
                .split("|")
                .some((id) =>
                    itemValues.some((v) =>
                        v.toLowerCase().includes(id.toLowerCase())
                    )
                )
        );
    }
    return false;
}

/** Returns items that pass every active filter. Filters with no committed selection are pass-through. */
export function applyFilters(
    items: unknown[],
    filters: FilterDefinition[],
    filterState: UniformFilterObject
): unknown[] {
    const activeFilters = filters.filter((filter) => {
        const subFilter = filterState[filter.id];
        return (
            subFilter &&
            Object.values(subFilter).some((opt) => opt.committedActive)
        );
    });

    if (activeFilters.length === 0) return items;

    return items.filter((item) =>
        activeFilters.every((filter) => {
            const subFilter = filterState[filter.id];
            if (!subFilter) return true;
            const selected = Object.entries(subFilter)
                .filter(([, opt]) => opt.committedActive)
                .map(([key]) => key);
            return matchValue(
                resolveFieldPath(item, filter.fieldPath),
                filter.matchType,
                selected
            );
        })
    );
}

/** Fuzzy search returning matched items in relevance order, using the weighted `searchConfiguration` keys. */
export function applySearch(
    items: unknown[],
    query: string,
    searchConfiguration?: SearchConfiguration
): unknown[] {
    const trimmedQuery = query.trim();
    if (!trimmedQuery || !hasSearchCriteria(searchConfiguration)) return items;

    return scoredSearch(items, trimmedQuery, searchConfiguration).map(
        (scored) => scored.item
    );
}

/** Search, then apply the featured boost (see `rankByFeaturedBoost`); with no query, a featured-first browse order. */
export function searchWithFeaturedBoost(
    items: unknown[],
    query: string,
    searchConfiguration: SearchConfiguration | undefined,
    featuredItemSlugs: string[]
): unknown[] {
    const trimmedQuery = query.trim();
    if (!trimmedQuery || !hasSearchCriteria(searchConfiguration)) {
        return prioritizeItemsBySlug(items, featuredItemSlugs);
    }

    const scored = scoredSearch(items, trimmedQuery, searchConfiguration);
    return rankByFeaturedBoost(
        scored,
        featuredItemSlugs,
        searchConfiguration.featuredBoost ?? DEFAULT_FEATURED_BOOST
    );
}

export type ScoredItem = { readonly item: unknown; readonly score: number };

/**
 * Featured items get a score `boost` (lower score = better), so they lead any
 * match within `boost` of their score; a match better by more than `boost`
 * stays ahead. Ties keep their incoming (relevance) order.
 */
export function rankByFeaturedBoost(
    scored: ScoredItem[],
    featuredItemSlugs: string[],
    boost: number
): unknown[] {
    if (featuredItemSlugs.length === 0) {
        return scored.map((s) => s.item);
    }

    const priorityBySlug = new Map(
        featuredItemSlugs.map((slug, index) => [slug, index])
    );
    const ranked = scored
        .map((s, index) => {
            const slugPriority = getSlugPriority(s.item, priorityBySlug);
            const isFeatured = slugPriority !== UNRANKED_PRIORITY;
            return {
                item: s.item,
                index, // stable tiebreak: keep relevance order for equal scores
                slugPriority, // featured order matches browse mode (prioritizeItemsBySlug)
                adjustedScore: s.score - (isFeatured ? boost : 0)
            };
        })
        .sort(
            (a, b) =>
                a.adjustedScore - b.adjustedScore ||
                a.slugPriority - b.slugPriority ||
                a.index - b.index
        );

    return ranked.map((r) => r.item);
}

function hasSearchCriteria(
    searchConfiguration: SearchConfiguration | undefined
): searchConfiguration is SearchConfiguration {
    return (
        searchConfiguration !== undefined && searchConfiguration.keys.length > 0
    );
}

/**
 * Weighted Fuse search. Each config key is indexed under a positional alias
 * (`key_0`, `key_1`, …) rather than its dot-path name. Fuse treats dot-paths as
 * nested lookups, and naively replacing dots with underscores would let two
 * distinct paths collide (e.g. `fields.name` and `fields_name`), silently
 * overwriting one key's data. Positional aliases sidestep both problems.
 */
function scoredSearch(
    items: unknown[],
    query: string,
    config: SearchConfiguration
): ScoredItem[] {
    type IndexedItem = Record<string, string[]> & {
        readonly __fuseOriginalItem__: unknown;
    };

    const aliasFor = (index: number) => `key_${index}`;

    const fuseKeys = config.keys.map((key, index) => ({
        name: aliasFor(index),
        weight: key.weight
    }));

    const indexedItems: IndexedItem[] = items.map((item) => {
        const indexed: Record<string, string[]> = {};
        config.keys.forEach((key, index) => {
            indexed[aliasFor(index)] = coerceToPrimitiveArray(
                resolveFieldPath(item, key.name)
            );
        });
        return { ...indexed, __fuseOriginalItem__: item } as IndexedItem;
    });

    return new Fuse(indexedItems, {
        keys: fuseKeys,
        threshold: config.threshold ?? DEFAULT_SEARCH_THRESHOLD,
        ignoreLocation: config.ignoreLocation ?? true,
        includeScore: true
    })
        .search(query)
        .map((result) => ({
            item: result.item.__fuseOriginalItem__,
            score: result.score ?? 0
        }));
}

/** Returns a new sorted array. No-op when sort is undefined. */
export function applySort(
    items: unknown[],
    sort: SortDefinition | undefined
): unknown[] {
    if (!sort) return items;

    const direction = sort.direction === "desc" ? -1 : 1;
    const valueType = sort.valueType ?? "string";

    return [...items].sort((a, b) => {
        const av = coerceForSort(
            resolveFieldPath(a, sort.fieldPath),
            valueType
        );
        const bv = coerceForSort(
            resolveFieldPath(b, sort.fieldPath),
            valueType
        );

        if (av === undefined && bv === undefined) return 0;
        if (av === undefined) return 1;
        if (bv === undefined) return -1;

        if (av < bv) return -1 * direction;
        if (av > bv) return 1 * direction;
        return 0;
    });
}

function coerceForSort(
    value: unknown,
    valueType: "string" | "number" | "date"
): string | number | undefined {
    const first = Array.isArray(value) ? value[0] : value;
    if (first === null || first === undefined) return undefined;

    if (valueType === "number") {
        const n = typeof first === "number" ? first : Number(first);
        return Number.isNaN(n) ? undefined : n;
    }

    if (valueType === "date") {
        const ms = new Date(String(first)).getTime();
        return Number.isNaN(ms) ? undefined : ms;
    }

    return String(first).toLowerCase();
}
